import { useState, useEffect, useRef } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Link,
  useParams,
  useNavigate,
} from "react-router-dom";
import { type User, type Site, type SiteWithState, api } from "./api";
import { useWebSocket } from "./websocket";
import { MKButton, ThemeProvider, MKToaster } from "@mytraai/mytrakit";
import Login from "./components/Login";
import SiteSelector from "./components/SiteSelector";
import SiteEditor from "./components/SiteEditor";
import ArtifactList from "./components/ArtifactList";

// Generate a unique selection key for a row (artifact + bot combination)
function makeSelectionKey(artifactId: number, botId: number | null): string {
  return `${artifactId}:${botId ?? "site"}`;
}
import PinEditor from "./components/PinEditor";
import DeployButton from "./components/DeployButton";

function AppContent() {
  const { siteId } = useParams<{ siteId: string }>();
  const navigate = useNavigate();

  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [sites, setSites] = useState<Site[]>([]);
  const [selectedSite, setSelectedSite] = useState<SiteWithState | null>(null);
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  const [showSiteEditor, setShowSiteEditor] = useState(false);
  const [editingSite, setEditingSite] = useState<Site | undefined>();

  // Track known pending selection keys to detect new vs existing
  const knownPendingKeysRef = useRef<Set<string>>(new Set());

  const ws = useWebSocket();

  // Helper to update site data and manage artifact selection intelligently
  const updateSiteData = (newSite: SiteWithState, isInitialLoad = false) => {
    // Collect pending selection keys from both site-level and bot-level
    const newPendingKeys: string[] = [];

    // Site-level pending artifacts (botId = null)
    Object.values(newSite.pending_artifact || {}).forEach((a) => {
      newPendingKeys.push(makeSelectionKey(a.artifact_id, null));
    });

    // Bot-level pending artifacts
    (newSite.bots || []).forEach((bot) => {
      Object.values(bot.pending_artifact || {}).forEach((a) => {
        newPendingKeys.push(makeSelectionKey(a.artifact_id, bot.bot_id));
      });
    });

    const newPendingKeySet = new Set(newPendingKeys);
    const knownKeys = knownPendingKeysRef.current;

    if (isInitialLoad) {
      // On initial load, select all pending rows
      setSelectedKeys(newPendingKeySet);
    } else {
      // Find newly added keys (in new but not previously known)
      const newlyAdded = newPendingKeys.filter((key) => !knownKeys.has(key));

      setSelectedKeys((prevSelected) => {
        const updated = new Set<string>();
        // Keep selected keys that are still pending
        prevSelected.forEach((key) => {
          if (newPendingKeySet.has(key)) {
            updated.add(key);
          }
        });
        // Add new keys as selected by default
        newlyAdded.forEach((key) => updated.add(key));
        return updated;
      });
    }

    // Update the ref with current pending keys
    knownPendingKeysRef.current = newPendingKeySet;
    setSelectedSite(newSite);
  };

  useEffect(() => {
    api
      .getMe()
      .then(setUser)
      .catch(() => setUser(null))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (user) {
      api.listSites().then(setSites);
    }
  }, [user]);

  useEffect(() => {
    if (siteId) {
      const id = parseInt(siteId, 10);
      if (!isNaN(id)) {
        api
          .getSite(id)
          .then((site) => {
            updateSiteData(site, true);
          })
          .catch(() => {
            setSelectedSite(null);
            setSelectedKeys(new Set());
            knownPendingKeysRef.current = new Set();
            navigate("/", { replace: true });
          });
      }
    } else {
      setSelectedSite(null);
      setSelectedKeys(new Set());
      knownPendingKeysRef.current = new Set();
    }
  }, [siteId, navigate]);

  // Handle WebSocket messages - intentionally using selectedSite?.site_id
  // to avoid re-running on every selectedSite state change
  useEffect(() => {
    if (!ws.lastMessage) return;

    const msg = ws.lastMessage;
    if (
      msg.type === "artifact_created" ||
      msg.type === "pin_updated" ||
      msg.type === "pin_deleted" ||
      msg.type === "release_created"
    ) {
      if (selectedSite) {
        api.getSite(selectedSite.site_id).then((site) => updateSiteData(site));
      }
    }

    // Handle sync status updates (refresh sites list and current site)
    if (msg.type === "sync_status_updated") {
      api.listSites().then(setSites);
      if (selectedSite) {
        api.getSite(selectedSite.site_id).then((site) => updateSiteData(site));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ws.lastMessage, selectedSite?.site_id]);

  const handleSelectionToggle = (selectionKey: string) => {
    setSelectedKeys((prev) => {
      const updated = new Set(prev);
      if (updated.has(selectionKey)) {
        updated.delete(selectionKey);
      } else {
        updated.add(selectionKey);
      }
      return updated;
    });
  };

  const handleDeploy = async () => {
    if (!selectedSite || selectedKeys.size === 0) return;
    // Extract unique artifact IDs from selection keys
    const artifactIds = new Set<number>();
    selectedKeys.forEach((key) => {
      const artifactId = parseInt(key.split(":")[0], 10);
      artifactIds.add(artifactId);
    });
    await api.deploy(selectedSite.site_id, Array.from(artifactIds));
    const site = await api.getSite(selectedSite.site_id);
    // After deploy, treat as initial load (select any remaining pending)
    updateSiteData(site, true);
  };

  const handleAddSite = () => {
    setEditingSite(undefined);
    setShowSiteEditor(true);
  };

  const handleEditSite = () => {
    if (selectedSite) {
      const site = sites.find((s) => s.site_id === selectedSite.site_id);
      setEditingSite(site);
      setShowSiteEditor(true);
    }
  };

  const handleSiteSave = async () => {
    setShowSiteEditor(false);
    setEditingSite(undefined);
    const updatedSites = await api.listSites();
    setSites(updatedSites);
    if (selectedSite) {
      const site = await api.getSite(selectedSite.site_id);
      updateSiteData(site);
    }
  };

  const handleSiteDelete = async () => {
    setShowSiteEditor(false);
    setEditingSite(undefined);
    const updatedSites = await api.listSites();
    setSites(updatedSites);
    if (
      selectedSite &&
      editingSite &&
      selectedSite.site_id === editingSite.site_id
    ) {
      navigate("/");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-content-tertiary">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Login />;
  }

  return (
    <div className="min-h-screen bg-surface-primary">
      <MKToaster />
      <header className="bg-surface-secondary border-b border-surface-tertiary">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h1 className="text-xl font-bold">
              <Link
                to="/"
                className="text-content-primary hover:text-content-secondary transition-colors"
              >
                easybutton
              </Link>
            </h1>
            {selectedSite && (
              <>
                <span className="text-content-tertiary">/</span>
                <span className="text-lg text-content-secondary">
                  {selectedSite.name}
                </span>
                <DeployButton
                  selectedCount={selectedKeys.size}
                  isProduction={selectedSite.is_production}
                  onDeploy={handleDeploy}
                />
              </>
            )}
          </div>
          <div className="flex items-center gap-4">
            {selectedSite && (
              <MKButton
                variant="ghost"
                size="sm"
                onClick={handleEditSite}
                title="Site settings"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                </svg>
              </MKButton>
            )}
            <span className="text-content-secondary">{user.email}</span>
            <MKButton
              variant="ghost"
              size="sm"
              onClick={() => api.logout().then(() => setUser(null))}
            >
              Logout
            </MKButton>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6">
        {!selectedSite && (
          <SiteSelector sites={sites} onAddSite={handleAddSite} />
        )}

        {selectedSite && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">
              <ArtifactList
                site={selectedSite}
                selectedKeys={selectedKeys}
                onToggle={handleSelectionToggle}
              />
            </div>

            <div>
              <PinEditor
                site={selectedSite}
                onUpdate={() =>
                  api
                    .getSite(selectedSite.site_id)
                    .then((site) => updateSiteData(site))
                }
              />
            </div>
          </div>
        )}
      </main>

      {showSiteEditor && (
        <SiteEditor
          site={editingSite}
          onSave={handleSiteSave}
          onCancel={() => setShowSiteEditor(false)}
          onDelete={editingSite ? handleSiteDelete : undefined}
        />
      )}
    </div>
  );
}

export default function App() {
  return (
    <ThemeProvider defaultTheme="dark" storageKey="easybutton-theme">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<AppContent />} />
          <Route path="/site/:siteId" element={<AppContent />} />
        </Routes>
      </BrowserRouter>
    </ThemeProvider>
  );
}
